import type { SupSinglePreparedRoute } from "@/components/Web3/Common/Route/utils/prepareProviderRoutes";
import React, { Fragment, useCallback } from "react";

import { useRouteState } from "@/components/Web3/Common/Route/State";
import { SupAttributeBadge, SupHeading, SupText } from "@repo/core-ui";
import UiLabelSuperPoolTag from "@/components/Ui/Label/SuperPool/Tag";
import { SupStopwatch, SupGas, SupBridge, SupReturn, SupTransfer, SupRadioCircle, SupRoute, SupRadioCircleMarked } from "@repo/core-ui";
import config from "@/../config/dapp.config";
import { classNames, formatBigNumberish, roundBigNumberValueString } from "@/utils";

interface Web3CommonRouteDefaultProps {
  route: SupSinglePreparedRoute;
  selectedRoute: SupSinglePreparedRoute | "direct" | undefined;
}

export default function Web3CommonRouteDefault({ route, selectedRoute }: Web3CommonRouteDefaultProps) {
  const { putVaultSelectedRoute } = useRouteState();

  const clickHandler = useCallback(() => {
    if (typeof selectedRoute !== "object" || selectedRoute.id !== route.id) {
      putVaultSelectedRoute?.(route.vault_id, route);
    }
  }, [putVaultSelectedRoute, route, selectedRoute]);

  const routeSuperpositions = selectedRoute !== "direct" ? route.superPosition : undefined;

  const isCurrentRoute = typeof selectedRoute === "object" && selectedRoute.id === route.id;

  return (
    <div className={classNames("w-full border-[.5px] border-neutral-700 bg-neutral p-4", !isCurrentRoute && "cursor-pointer hover:bg-primary-200/10")} onClick={clickHandler}>
      <div className="mb-6 flex w-full items-center justify-start gap-1">
        {route.badges.map(badge => (
          <SupAttributeBadge
            key={`route-${route.id}-${badge}`}
            label={`${badge === "time" ? "fastest" : badge === "gas" ? "cheapest" : "High return"}`}
            color={badge === "time" ? "primary" : "default"}
            className={classNames(badge === "gas" && "bg-neutral-800 text-neutral-900")}
          />
        ))}
        {route.isERC20 && <UiLabelSuperPoolTag isSmall />}
        {isCurrentRoute ? <SupRadioCircleMarked className="ml-auto h-[18px] w-[18px] fill-neutral-800" /> : <SupRadioCircle className="ml-auto h-5 w-5" />}
      </div>

      {/*Receiving amount*/}
      <div className="flex flex-col items-center justify-start self-stretch">
        {routeSuperpositions && (
          <div className="flex flex-col items-start justify-start self-stretch">
            <div className="mb-1 inline-flex h-5 items-center justify-between self-stretch">
              <SupHeading variant="h5">{formatBigNumberish(routeSuperpositions.amount, route.vaultDecimals)}</SupHeading>
              <SupText variant="subtitle2">{routeSuperpositions.symbol}</SupText>
            </div>
            <SupText variant="subtitle2" className="mt-1 h-5">
              ${roundBigNumberValueString(routeSuperpositions.usd_value, config.decimalsRoundingLimit)}
              {!route.isERC20 && <Fragment>{route.difference}%</Fragment>}
            </SupText>
          </div>
        )}

        {/*SERVICE info*/}
        {route.service && (
          <div className="inline-flex h-5 items-center justify-between self-stretch">
            <div className="flex items-center justify-start gap-1">
              <SupRoute />
              <SupText variant="body2">Service</SupText>
            </div>
            <div className="flex items-center justify-end gap-1">
              <div className="flex h-5 w-5 items-center justify-center">
                <img className="shrink grow basis-0 self-stretch rounded-full bg-[#F6F6F9]" src={route.service.logo} alt={route.service.name} />
              </div>
              <SupText variant="caption" weight="bold">
                {route.service.name}
              </SupText>
            </div>
          </div>
        )}

        {/*Time estimation*/}
        {route.estimatedTime && (
          <div className="inline-flex h-5 items-center justify-between self-stretch">
            <div className="flex items-center justify-start gap-1">
              <SupStopwatch />
              <SupText variant="body2">Bridge Time</SupText>
            </div>
            <SupText variant="body2" family="sans" weight="bold">
              {route.estimatedTime}
            </SupText>
          </div>
        )}

        {/*Gas info*/}
        <div className="inline-flex h-5 items-center justify-between self-stretch">
          <div className="flex items-center justify-start gap-1">
            <SupGas />
            <SupText variant="body2">Total gas cost</SupText>
          </div>
          <SupText variant="body2" family="sans" weight="bold">
            ${roundBigNumberValueString(route.fees.gasCost, config.decimalsRoundingLimit)}
          </SupText>
        </div>

        {/*CrossChain fee*/}
        {!!route.fees.crossChainFee && (
          <div className="inline-flex h-5 items-center justify-between self-stretch">
            <div className="flex items-center justify-start gap-1">
              <SupGas />
              <SupText variant="body2">Cross-chain fee</SupText>
            </div>
            <SupText variant="body2" family="sans" weight="bold">
              ${roundBigNumberValueString(`${route.fees.crossChainFee}`, config.decimalsRoundingLimit)}
            </SupText>
          </div>
        )}

        {/*minimum output*/}
        <div className="inline-flex h-5 items-center justify-between self-stretch">
          <div className="flex items-center justify-start gap-1">
            <SupReturn />
            <SupText variant="body2">Minimum output</SupText>
          </div>
          <SupText variant="body2" family="sans" weight="bold">
            {roundBigNumberValueString(route.toMinAmount.toString(), config.decimalsBigRoundingLimit)} {route.toToken.symbol}
          </SupText>
        </div>

        {/*BRIDGING info*/}
        {route.bridge && (
          <div className="inline-flex h-5 items-center justify-between self-stretch">
            <div className="flex items-center justify-start gap-1">
              <SupBridge />
              <SupText variant="body2">Token Bridge</SupText>
            </div>
            <div className="flex items-center justify-end gap-1">
              <div className="flex h-5 w-5 items-center justify-center">
                <img className="shrink grow basis-0 self-stretch rounded-full bg-[#F6F6F9]" src={route.bridge.logo} alt={route.bridge.name} />
              </div>
              <SupText variant="caption">{route.bridge.name}</SupText>
            </div>
          </div>
        )}

        {/*DEX (SWAP of the tokens)*/}
        {route.dex && (
          <div className="inline-flex h-5 items-center justify-between self-stretch">
            <div className="flex items-center justify-start gap-1">
              <SupTransfer />
              <SupText variant="body2">DEX</SupText>
            </div>
            <div className="flex items-center justify-end gap-1">
              <div className="flex h-5 w-5 items-center justify-center">
                <img className="shrink grow basis-0 self-stretch rounded-full bg-[#F6F6F9]" src={route.dex.logo} alt={route.dex.name} />
              </div>
              <SupText variant="caption">{route.dex.name}</SupText>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
